import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { CheckmarkCircleRegular, SearchRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { useApi } from "@/hooks/useApi";
import { ProjectsApi } from "@/services/api/projects-api";
import { ProjectTemplatesApi } from "@/services/api/project-templates-api";
import { useAuth } from "@/services/auth/use-auth";
import type { TriyoProject, TriyoProjectTemplate } from "@/types/models";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import { resolveLaunchUrl } from "@/utils/launch-url";
import {
  CREATE_DEAL_ACCESS_DENIED,
  DEAL_PRIORITIES,
  type DealPersonOption,
  type DealPriority,
  addDaysToDateInput,
  buildDealPayload,
  filterRoster,
  isForbiddenError,
  normalizeRoster,
  parseDateInput,
  parseApiErrorMessage,
  toDateInputValue,
} from "./create-deal-helpers";
import "./CreateDeal.scss";

export interface CreateDealProps {
  /** Invoked on Cancel and on Done from the success screen (dialog/back host). */
  onClose?: () => void;
  /** Invoked after the API confirms creation (e.g. so the Deals list can refresh). */
  onCreated?: (project: TriyoProject) => void;
}

const STEPS = ["Deal Information", "Senior Deal Team", "Analyst/Associate Staffing"] as const;

interface CreateDealSetup {
  templates: TriyoProjectTemplate[];
  defaultTemplate: TriyoProjectTemplate | null;
}

/** Loads the roster (fatal on failure) and templates (optional, best-effort). */
async function fetchSetup(): Promise<CreateDealSetup> {
  const [templates, defaultTemplate] = await Promise.allSettled([
    ProjectTemplatesApi.getTemplatesForProject(),
    ProjectTemplatesApi.getDefaultTemplate(),
  ]);
  return {
    templates: templates.status === "fulfilled" ? templates.value : [],
    defaultTemplate: defaultTemplate.status === "fulfilled" ? defaultTemplate.value : null,
  };
}

function StepBars({ step }: { step: number }): JSX.Element {
  return (
    <div className="create-deal__step-bars" role="list" aria-label={`Step ${step} of ${STEPS.length}`}>
      {STEPS.map((label, index) => (
        <div
          key={label}
          role="listitem"
          aria-label={label}
          aria-current={index + 1 === step ? "step" : undefined}
          className={`create-deal__step-bar${index + 1 <= step ? " create-deal__step-bar--filled" : ""}`}
        />
      ))}
    </div>
  );
}

interface RosterListProps {
  options: DealPersonOption[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}

function RosterList({ options, selected, onToggle }: RosterListProps): JSX.Element {
  if (options.length === 0) {
    return <p className="create-deal__no-matches">No matches</p>;
  }
  return (
    <div className="create-deal__roster">
      {options.map((person) => (
        <label key={person.key} className="create-deal__roster-row">
          <input
            type="checkbox"
            checked={selected.has(person.key)}
            onChange={() => onToggle(person.key)}
          />
          <span className="create-deal__roster-text">
            <span className="create-deal__roster-name">{person.name}</span>
            {person.detail ? <span className="create-deal__roster-detail"> · {person.detail}</span> : null}
          </span>
        </label>
      ))}
    </div>
  );
}

export function CreateDeal({ onClose, onCreated }: CreateDealProps): JSX.Element {
  const { user } = useAuth();

  const rosterFetcher = useCallback(async () => {
    try {
      return await ProjectsApi.getUsersAndGroups();
    } catch (error: unknown) {
      if (isForbiddenError(error)) throw new Error(CREATE_DEAL_ACCESS_DENIED);
      throw error instanceof Error ? error : new Error("The Deal roster could not be loaded.");
    }
  }, []);
  const roster = useApi(rosterFetcher);

  const setupFetcher = useCallback(() => fetchSetup(), []);
  const setup = useApi(setupFetcher);
  const templates = useMemo(
    () => (setup.data?.templates ?? []).filter((template) => !!template.Id && !!template.ProjectType),
    [setup.data]
  );

  const people = useMemo(() => normalizeRoster(roster.data), [roster.data]);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState(() => toDateInputValue(new Date()));
  const [end, setEnd] = useState("");
  const [priority, setPriority] = useState<DealPriority>("Medium");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [ownerKeys, setOwnerKeys] = useState<Set<string>>(new Set());
  const [memberKeys, setMemberKeys] = useState<Set<string>>(new Set());
  const [ownerSearch, setOwnerSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [created, setCreated] = useState<TriyoProject | null>(null);

  const clearError = (key: string) => setErrors((prev) => ({ ...prev, [key]: undefined }));

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.Id === templateId) ?? null,
    [templates, templateId]
  );

  // Preselect the tenant default template and derive End = Start + Days, as Triyo.Web does.
  useEffect(() => {
    if (defaultsApplied || setup.loading) return;
    setDefaultsApplied(true);
    const fallback = setup.data?.defaultTemplate;
    const match = fallback?.Id ? templates.find((template) => template.Id === fallback.Id) : undefined;
    if (match?.Id) {
      setTemplateId(match.Id);
      if (match.Days && match.Days > 0) {
        setEnd((current) => current || addDaysToDateInput(toDateInputValue(new Date()), match.Days ?? 0));
      }
    }
  }, [defaultsApplied, setup.loading, setup.data, templates]);

  // Default the signed-in user as an owner once the roster arrives.
  useEffect(() => {
    if (!user?.Id) return;
    const key = `u-${user.Id}`;
    if (!people.some((person) => person.key === key)) return;
    setOwnerKeys((current) => {
      if (current.size > 0) return current;
      return new Set([key]);
    });
  }, [people, user?.Id]);

  const toggleKey = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (key: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onTemplateChange = (value: string) => {
    const id = value ? Number(value) : null;
    setTemplateId(id);
    const template = templates.find((entry) => entry.Id === id);
    if (template?.Days && template.Days > 0 && parseDateInput(start)) {
      setEnd(addDaysToDateInput(start, template.Days));
    }
  };

  const nextFromStep1 = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Required";
    if (!parseDateInput(start)) next.start = "Required";
    if (!parseDateInput(end)) next.end = "Required";
    const startDate = parseDateInput(start);
    const endDate = parseDateInput(end);
    if (startDate && endDate && endDate < startDate) next.end = "End date cannot be before Start date";
    setErrors(next);
    if (Object.keys(next).length === 0) setStep(2);
  };

  const nextFromStep2 = () => {
    if (ownerKeys.size === 0) {
      setErrors({ owners: "Select at least one senior deal team member." });
      return;
    }
    setErrors({});
    setStep(3);
  };

  const handleCreate = async () => {
    if (memberKeys.size === 0) {
      setErrors({ members: "Select at least one analyst or associate." });
      return;
    }
    setErrors({});
    setSubmitError("");
    setCreating(true);
    try {
      const payload = buildDealPayload({
        name,
        description,
        start,
        end,
        priority,
        template: selectedTemplate,
        owners: people.filter((person) => ownerKeys.has(person.key)),
        members: people.filter((person) => memberKeys.has(person.key)),
      });
      const project = await ProjectsApi.addProject(payload);
      setCreated(project);
      onCreated?.(project);
    } catch (error: unknown) {
      setSubmitError(parseApiErrorMessage(error, "The Deal could not be created. Please try again."));
    } finally {
      setCreating(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setName("");
    setDescription("");
    setStart(toDateInputValue(new Date()));
    setEnd("");
    setPriority("Medium");
    setTemplateId(null);
    setDefaultsApplied(false);
    setOwnerKeys(new Set());
    setMemberKeys(new Set());
    setOwnerSearch("");
    setMemberSearch("");
    setErrors({});
    setSubmitError("");
    setCreated(null);
  };

  const handleDone = () => {
    if (onClose) onClose();
    else resetAll();
  };

  const projectsUrl = resolveLaunchUrl("projects");
  const openInTriyo = () => {
    if (projectsUrl) window.open(projectsUrl, "_blank", "noopener,noreferrer");
  };

  if (roster.loading) {
    return (
      <div className="create-deal__state" role="status">
        <FluentSpinner label="Loading Deal setup…" />
      </div>
    );
  }

  if (roster.error === CREATE_DEAL_ACCESS_DENIED) {
    return (
      <div className="create-deal__state" role="alert">
        <h3>Deal creation</h3>
        <p>
          Your TRIYO account does not have permission to view Deal members, which is required to
          create a Deal. Contact your TRIYO administrator for access.
        </p>
        {onClose ? (
          <button type="button" className="create-deal__button" onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
    );
  }

  if (roster.error) {
    return (
      <div className="create-deal__state" role="alert">
        <p>{roster.error}</p>
        <button type="button" className="create-deal__button" onClick={() => void roster.refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (created) {
    const ownerNames = people.filter((person) => ownerKeys.has(person.key)).map((person) => person.name);
    const memberNames = people.filter((person) => memberKeys.has(person.key)).map((person) => person.name);
    const metaParts = [
      created.Id ? `Deal #${created.Id}` : null,
      `${formatLongCalendarDate(start)} – ${formatLongCalendarDate(end)}`,
      priority,
    ].filter(Boolean);
    return (
      <div className="create-deal__success" role="status">
        <CheckmarkCircleRegular className="create-deal__success-icon" />
        <h3>{created.Name || name.trim()} created</h3>
        <p className="create-deal__success-meta">{metaParts.join(" · ")}</p>
        <div className="create-deal__success-summary">
          <p>
            <strong>Senior deal team ({ownerNames.length}):</strong> {ownerNames.join(", ")}
          </p>
          <p>
            <strong>Deal members ({memberNames.length}):</strong> {memberNames.join(", ")}
          </p>
        </div>
        <div className="create-deal__success-actions">
          <button type="button" className="create-deal__button" onClick={handleDone}>
            Done
          </button>
          {projectsUrl ? (
            <button type="button" className="create-deal__button create-deal__button--primary" onClick={openInTriyo}>
              Open in TRIYO
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="create-deal">
      <div className="create-deal__body">
        {step === 1 ? (
          <>
            <div className="create-deal__field">
              <label htmlFor="create-deal-name">
                Deal Name <span className="create-deal__required">*</span>
              </label>
              <input
                id="create-deal-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearError("name");
                }}
                placeholder="e.g. Deal Maple"
              />
              {errors.name ? <p className="create-deal__field-error">{errors.name}</p> : null}
            </div>

            <div className="create-deal__field">
              <label htmlFor="create-deal-description">Description</label>
              <textarea
                id="create-deal-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief mandate summary"
                rows={3}
              />
            </div>

            <div className="create-deal__grid">
              <div className="create-deal__field">
                <label htmlFor="create-deal-start">
                  Start Date <span className="create-deal__required">*</span>
                </label>
                <input
                  id="create-deal-start"
                  type="date"
                  value={start}
                  max={end || undefined}
                  onChange={(e) => {
                    setStart(e.target.value);
                    clearError("start");
                  }}
                />
                {errors.start ? <p className="create-deal__field-error">{errors.start}</p> : null}
              </div>
              <div className="create-deal__field">
                <label htmlFor="create-deal-end">
                  End Date <span className="create-deal__required">*</span>
                </label>
                <input
                  id="create-deal-end"
                  type="date"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => {
                    setEnd(e.target.value);
                    clearError("end");
                  }}
                />
                {errors.end ? <p className="create-deal__field-error">{errors.end}</p> : null}
              </div>
            </div>

            <div className="create-deal__grid">
              {templates.length > 0 ? (
                <div className="create-deal__field">
                  <label htmlFor="create-deal-template">Template</label>
                  <select
                    id="create-deal-template"
                    value={templateId ?? ""}
                    onChange={(e) => onTemplateChange(e.target.value)}
                  >
                    <option value="">None</option>
                    {templates.map((template) => (
                      <option key={template.Id} value={template.Id}>
                        {template.ProjectType}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="create-deal__field">
                <label htmlFor="create-deal-priority">Priority</label>
                <select
                  id="create-deal-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as DealPriority)}
                >
                  {DEAL_PRIORITIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="create-deal__step-hint">Select the senior deal team for this deal.</p>
            <div className="create-deal__search">
              <SearchRegular />
              <input
                value={ownerSearch}
                onChange={(e) => setOwnerSearch(e.target.value)}
                placeholder="Search senior deal team"
                aria-label="Search senior deal team"
              />
            </div>
            <RosterList
              options={filterRoster(people, ownerSearch)}
              selected={ownerKeys}
              onToggle={(key) => {
                toggleKey(setOwnerKeys)(key);
                clearError("owners");
              }}
            />
            {errors.owners ? <p className="create-deal__field-error">{errors.owners}</p> : null}
            <p className="create-deal__selected-count">{ownerKeys.size} selected</p>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <p className="create-deal__step-hint">Select the analysts and associates to staff on this deal.</p>
            <div className="create-deal__search">
              <SearchRegular />
              <input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search analysts and associates"
                aria-label="Search analysts and associates"
              />
            </div>
            <RosterList
              options={filterRoster(people, memberSearch)}
              selected={memberKeys}
              onToggle={(key) => {
                toggleKey(setMemberKeys)(key);
                clearError("members");
              }}
            />
            {errors.members ? <p className="create-deal__field-error">{errors.members}</p> : null}
            <p className="create-deal__selected-count">{memberKeys.size} selected</p>
          </>
        ) : null}
      </div>

      <div className="create-deal__footer">
        {submitError ? (
          <p className="create-deal__submit-error" role="alert">
            {submitError}
          </p>
        ) : null}
        <div className="create-deal__footer-row">
          <span className="create-deal__progress">
            <span>
              Step {step} of {STEPS.length}
            </span>
            <StepBars step={step} />
          </span>
          <div className="create-deal__footer-actions">
            {step === 1 ? (
              onClose ? (
                <button type="button" className="create-deal__button" onClick={onClose}>
                  Cancel
                </button>
              ) : null
            ) : (
              <button
                type="button"
                className="create-deal__button"
                disabled={creating}
                onClick={() => {
                  setErrors({});
                  setSubmitError("");
                  setStep(step - 1);
                }}
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button type="button" className="create-deal__button create-deal__button--primary" onClick={nextFromStep1}>
                Next
              </button>
            ) : null}
            {step === 2 ? (
              <button type="button" className="create-deal__button create-deal__button--primary" onClick={nextFromStep2}>
                Next
              </button>
            ) : null}
            {step === 3 ? (
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                disabled={creating}
                onClick={() => void handleCreate()}
              >
                {creating ? "Creating…" : "Create Deal"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
